import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { formatNumber } from "@danishagro/shared/src/helpers/formatNumber.helper";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { DA_ButtonLink } from "@danishagro/shared/src/components/atoms/ButtonLink/ButtonLink.component";
import { DA_IconNames } from "@danishagro/shared/src/components/atoms/Icon/Icon.component";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { useScreen } from "@danishagro/shared/src/contexts/screen.context";
import S from "./DiscountTableDesktop.module.scss";

interface DiscountRow {
    quantityFrom: number;
    quantityTo?: number;
    price: number;
}

interface DiscountTableDesktopProps {
    discountTable: DiscountRow[];
    currentQuantity: number;
    onRowClick?: (quantity: number) => void;
    clickableRowsDesktop?: boolean;
    priceCurrency?: string;
}

export const DiscountTableDesktop = ({
    discountTable,
    currentQuantity,
    onRowClick,
    clickableRowsDesktop,
    priceCurrency,
}: DiscountTableDesktopProps) => {
    const { getDictionaryString } = useTranslations();
    const { currentCulture } = useAppData(); // If you need the currentCulture from your context
    const { isMobileOrTablet } = useScreen();

    const [isExpanded, setIsExpanded] = useState(false);

    // Find the active row (the row that matches `currentQuantity`)
    const getActiveRowIndex = () => {
        return discountTable.findIndex(
            (item) =>
                currentQuantity >= item.quantityFrom &&
                currentQuantity <= (item.quantityTo || Infinity)
        );
    };

    const activeRowIndex = getActiveRowIndex();

    useEffect(() => {
        // If the user’s activeRowIndex is 2 or more, show all rows
        if (activeRowIndex >= 2 && !isExpanded) {
            setIsExpanded(true);
        }
    }, [activeRowIndex, isExpanded]);

    const schemaMarkup = {
        "@context": "https://schema.org",
        "@type": "AggregateOffer",
        offers: discountTable.map((item) => ({
            "@type": "Offer",
            price: formatNumber(item.price, currentCulture, { decimals: 2 }),
            priceCurrency: priceCurrency,
            eligibleQuantity: {
                "@type": "QuantitativeValue",
                value: item.quantityFrom,
            },
        })),
    };

    // Decide how many rows to show
    const rowsToShow = isExpanded ? discountTable : discountTable.slice(0, 3);

    return (
        <div className={S.tableContainer}>
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
            </Helmet>
            <table className={S.discountTable}>
                <thead>
                    <tr>
                        <th className={S.titleLeft}>
                            {getDictionaryString("amountDiscount.amountDiscount")}
                        </th>
                        <th className={S.titleRight}>
                            {getDictionaryString("amountDiscount.pricePerPcs")}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {rowsToShow.map((item, index) => (
                        <tr
                            key={index}
                            className={index === activeRowIndex && S.activeRow}
                            onClick={() => {
                                if (clickableRowsDesktop && !isMobileOrTablet && onRowClick) {
                                    onRowClick(item.quantityFrom);
                                }
                            }}
                        >
                            <td className={S.firstColumn}>
                                {item.quantityFrom} {getDictionaryString("pcs")}
                            </td>
                            <td className={cn(S.alignRight, S.secondColumn)}>
                                {formatNumber(item.price, currentCulture, { decimals: 2 })}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Expand button if more than 3 discount rows exist */}
            {!isExpanded && discountTable.length > 3 && (
                <DA_ButtonLink
                    title={getDictionaryString("amountDiscount.showMore")}
                    className={S.expandButton}
                    onClick={() => setIsExpanded(true)}
                    icon={DA_IconNames.ChevronSmallDown}
                />
            )}
        </div>
    );
};
